import React, { useState, useRef, useEffect } from "react";
import { IncButton, IncTextfield, IncCheckbox, IncSmartText } from "@inception/ui";
import { isNil } from "lodash";
import { PopperOption } from "./types";

export interface IncDropDownV2ContentProps<T = string> {
  children?: React.ReactNode;
  popperClassName?: string;
  width?: number;
  searchInputValue: string;
  onSearchInputChange: (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => void;
  searchPlaceholder: string;
  multi: boolean;
  filtered: Array<PopperOption<T>>;
  selectAll: () => void;
  draft: T[];
  listHeight: number;
  renderRow: (option: PopperOption<T>, level?: number) => React.ReactNode;
  onChange?: (next: T[]) => void;
  noSelectionYet: boolean;
  handleDone: () => void;
  setOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
  actionJSX?: React.ReactNode;
  headerJSX?: React.ReactNode;
  getAllEnabledLeafValues: (opts: Array<PopperOption<T>>) => T[];
}

const IncDropDownV2Content = <T,>({
  children,
  popperClassName = "",
  width,
  searchInputValue,
  onSearchInputChange,
  searchPlaceholder,
  multi,
  filtered,
  selectAll,
  draft,
  listHeight,
  renderRow,
  onChange,
  noSelectionYet,
  handleDone,
  setOpen,
  actionJSX,
  headerJSX,
  getAllEnabledLeafValues
}: IncDropDownV2ContentProps<T>) => {
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true);

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 1000);
    };

    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener("scroll", handleScroll);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  if (children) {
    return (
      <div
        className={`inc-dropdown-v2 ${popperClassName}`}
        style={{ width: `${width ?? 350}px` }}
      >
        {children}
      </div>
    );
  }

  // Get all enabled leaf values to check if all are selected
  const allEnabledLeafValues = getAllEnabledLeafValues(filtered);
  const allSelected = allEnabledLeafValues.length > 0 && allEnabledLeafValues.every(v => draft.includes(v));

  return (
    <div
      className={`inc-dropdown-v2 ${popperClassName} padding16 ${isScrolling ? "is-scrolling" : ""}`}
      style={{ width: `${width ?? 350}px` }}
    >
      {headerJSX && <div className="inc-dropdown-v2-header">{headerJSX}</div>}
      <IncTextfield
        onChange={onSearchInputChange}
        placeholder={searchPlaceholder}
        size="small"
        startIcon="magnifying-glass"
        value={searchInputValue}
      />

      {multi && filtered.length > 0 && (
        <div
          className="inc-flex-row inc-flex-center-vertical flex-gap-8 inc-text-body-semibold select-all"
          onClick={selectAll}
          style={{ marginTop: 8 }}
        >
          <IncCheckbox
            checked={allSelected}
            onChange={selectAll}
            skipChangeOnLabelClick
          />
          <IncSmartText
            numLines={1}
            text="Select All"
          />
        </div>
      )}

      <div
        className="overflow-y-auto"
        ref={scrollRef}
        style={{
          maxHeight: listHeight,
          marginTop: multi && filtered.length > 0 ? 0 : 8
        }}
      >
        {filtered.length ? (
          filtered.map(opt => renderRow(opt, 0))
        ) : (
          <div className="drop-down-no-match">
            <div className="inc-flex-row inc-flex-center-vertical inc-text-secondary inc-subtext">No matches</div>
          </div>
        )}
      </div>

      {onChange && (multi || filtered.some(opt => opt.children && opt.children.length > 0)) && (
        <div className="inc-flex-row flex-gap-16 paddingTp8">
          <IncButton
            className={`width-fit-content${noSelectionYet ? " inc-btn--ghost" : ""}`}
            color="primary"
            disabled={noSelectionYet}
            onClick={handleDone}
          >
            Done
          </IncButton>
          <IncButton
            className="width-fit-content"
            color="link"
            onClick={() => setOpen(false)}
          >
            Cancel
          </IncButton>
          {multi && filtered.length > 0 && noSelectionYet && !isNil(actionJSX) && actionJSX}
        </div>
      )}
    </div>
  );
};

export default IncDropDownV2Content;
